/**
 * useSmsHandoff Hook
 * Manages the token-based handoff flow behind the SMS links.
 *
 * SPEC-2300: on the Non-LT / Failed LT lane, loading the claim link no longer
 * changes the row — it stays `pending` for the full 72-hour window. There is no
 * soft hold to count down and no call-initiated extension; the agent claims
 * outright from the landing page's Call lead / Text lead modal.
 *
 * claimWithNotes / releaseWithNotes stay for the LT-connected lane, which still
 * runs the "how did the call go?" follow-up text into /handoff/notes.
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../graphql/users/useUser';
import {
  validateHandoffToken,
  declineHandoffByToken,
  claimHandoff,
  claimWithNotes,
  releaseWithNotes,
} from '../../api/handoff';
import { ValidateTokenResponse, SmsHandoffDetails } from '../../types/handoff.types';
import { HANDOFF_ACTION_SOURCE } from '../../utils/handoffTimeline';

interface UseSmsHandoffOptions {
  token?: string;
  onSuccess?: (contactId: string) => void;
  onError?: (error: string) => void;
}

interface UseSmsHandoffReturn {
  // Data
  handoffDetails: SmsHandoffDetails | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  handleClaim: (notes?: string) => Promise<boolean>;
  handleDecline: () => Promise<void>;
  handleClaimWithNotes: (notes: string) => Promise<void>;
  handleReleaseWithNotes: (notes: string) => Promise<void>;
  openNotesModal: () => void;
  closeNotesModal: () => void;
  showNotesModal: boolean;

  // Status
  isClaiming: boolean;
  isDeclining: boolean;
  isSubmittingNotes: boolean;
}

export const useSmsHandoff = (options: UseSmsHandoffOptions = {}): UseSmsHandoffReturn => {
  const { token, onSuccess, onError } = options;
  const queryClient = useQueryClient();
  const { user } = useUser();
  const loggedInName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined
    : undefined;

  const [showNotesModal, setShowNotesModal] = useState(false);

  // Check if this is test mode
  const isTestMode = token === 'test';
  // The post-call page only exists for live-transfer leads, so its mock is one:
  // reached by call, with the transfer signal the real rows carry.
  const isLiveTransferMock = isTestMode && window.location.pathname.includes('/handoff/notes');

  // Mock data for local visual QA (camelCase, matching the backend shape)
  const mockHandoffDetails: SmsHandoffDetails | null = isTestMode
    ? {
        handoffId: 'test-handoff-id',
        contactId: 'ibDFP65KJOCbCQbxhIqq',
        contactName: 'John Doe',
        contactPhone: '+1 (555) 123-4567',
        contactEmail: 'john.doe@example.com',
        organizationId: 'test-org',
        hotnessScore: 85,
        hotnessLevel: 'urgent',
        // `signals` is what the chips render from; free-text reasons alone
        // produce none, which left the mock showing an empty Signals row.
        // The claim landing page shows exactly the five chips under review:
        // callback request, speak with agent, buyer consultation, listing
        // consultation, renter. The post-call mock adds liveTransferCompleted
        // because it is lt_connected — a row on that lane always carries the
        // signal that put it there, and it is the only mock coverage of the
        // legacy live-transfer chip. Real rows carry more of the legacy set
        // still (buying intent, urgency, pre-approved) and render all of it.
        signals: {
          ...(isLiveTransferMock ? { liveTransferCompleted: true } : {}),
          callback: true,
          agentRequest: true,
          appointmentRequest: true,
          listingConsultation: true,
          renter: true,
        },
        reasons: ['Buying intent detected', 'High engagement', 'Requested callback'],
        conversationSummary:
          'John asked over text for a callback about the Country Club Plaza townhome. ' +
          'Wants a weekend tour.',
        status: 'pending',
        // The two mocks differ so both readings of the "Last engaged" row can
        // be seen: the claim landing page via sms, the post-call page via call.
        handoffLane: isLiveTransferMock ? 'lt_connected' : 'non_lt',
        channelOfOrigin: isLiveTransferMock ? 'voice' : 'text',
        location: { city: 'Kansas City', state: 'MO' },
      }
    : null;

  // Validate token and get handoff details (skip if test mode)
  // Note: We only fetch once - no refetchInterval to avoid repeated API calls
  const {
    data: validationData,
    isLoading,
    error: queryError,
  } = useQuery<ValidateTokenResponse>({
    queryKey: ['sms-handoff', token],
    queryFn: () => {
      if (!token) throw new Error('No token provided');
      return validateHandoffToken(token);
    },
    enabled: !!token && !isTestMode, // Disable query in test mode
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity, // Don't refetch automatically
  });

  // A handoff in the payload IS the success signal. The endpoint returns 200
  // with no `success` field, so gating on the flag sent every valid link to the
  // error page as `reason=invalid` — which reads as "that SMS link expired".
  const handoffDetails = isTestMode ? mockHandoffDetails : validationData?.handoff || null;

  // Claim from the landing page: pending -> claimed. The backend does not
  // distinguish call from text intent, so both buttons land here.
  const claimMutation = useMutation({
    mutationFn: async (notes?: string) => {
      if (!handoffDetails) throw new Error('No handoff details');
      if (isTestMode) return { success: true };
      // No agentId on purpose. A claim link is a team offer (SPEC-2076):
      // whoever opens it becomes the assigned agent, and the backend attributes
      // the claim to the authenticated caller when the field is absent. Sending
      // the token's agent id instead would 403 ("agentId must match
      // authenticated user") whenever a teammate claims a link we texted to
      // someone else.
      return claimHandoff({
        organizationId: handoffDetails.organizationId,
        handoffId: handoffDetails.handoffId,
        contactId: handoffDetails.contactId,
        agentName: loggedInName || handoffDetails.agentName,
        source: HANDOFF_ACTION_SOURCE.smsLandingPage,
        ...(notes?.trim() ? { notes: notes.trim() } : {}),
      });
    },
  });

  // Mutation for declining handoff
  const declineMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('No token');
      return declineHandoffByToken(token);
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess?.('');
      } else {
        onError?.(result.error || 'Failed to decline handoff');
      }
    },
    onError: (error: unknown) => {
      onError?.(error instanceof Error ? error.message : 'Failed to decline handoff');
    },
  });

  // Mutation for claiming with notes (LT-connected lane, /handoff/notes route)
  const claimWithNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      if (!token || !handoffDetails) throw new Error('No token or handoff details');
      return claimWithNotes({
        token,
        handoffId: handoffDetails.handoffId,
        organizationId: handoffDetails.organizationId,
        notes,
        agentId: handoffDetails.agentId || '',
        agentName: loggedInName || handoffDetails.agentName || undefined,
        source: HANDOFF_ACTION_SOURCE.smsLandingPage,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        setShowNotesModal(false);
        // Use contactId from handoffDetails since API returns handoffId instead
        onSuccess?.(handoffDetails?.contactId || '');
        // Clear handoff state
        queryClient.removeQueries({ queryKey: ['sms-handoff', token] });
      } else {
        onError?.(result.error || 'Failed to claim lead');
      }
    },
  });

  // Mutation for releasing with notes (LT-connected lane)
  const releaseMutation = useMutation({
    mutationFn: async (notes: string) => {
      if (!token || !handoffDetails) throw new Error('No token or handoff details');
      return releaseWithNotes({
        token,
        handoffId: handoffDetails.handoffId,
        organizationId: handoffDetails.organizationId,
        notes,
        agentId: handoffDetails.agentId || '',
        agentName: loggedInName || handoffDetails.agentName || undefined,
        source: HANDOFF_ACTION_SOURCE.smsLandingPage,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        setShowNotesModal(false);
        onSuccess?.(''); // Call onSuccess for release (no contactId needed)
        // Clear handoff state
        queryClient.removeQueries({ queryKey: ['sms-handoff', token] });
      } else {
        onError?.(result.error || 'Failed to release lead');
      }
    },
  });

  // Resolves true only once the row is actually claimed, so the caller opens the
  // dialer or messages app after the claim lands rather than optimistically.
  const handleClaim = useCallback(
    async (notes?: string): Promise<boolean> => {
      try {
        const result = await claimMutation.mutateAsync(notes);
        if (result.success) return true;
        onError?.(result.error || 'Failed to claim lead');
        return false;
      } catch (error) {
        onError?.(error instanceof Error ? error.message : 'Failed to claim lead');
        return false;
      }
    },
    [claimMutation, onError]
  );

  const handleDecline = useCallback(async () => {
    await declineMutation.mutateAsync();
  }, [declineMutation]);

  const handleClaimWithNotes = useCallback(
    async (notes: string) => {
      await claimWithNotesMutation.mutateAsync(notes);
    },
    [claimWithNotesMutation]
  );

  const handleReleaseWithNotes = useCallback(
    async (notes: string) => {
      await releaseMutation.mutateAsync(notes);
    },
    [releaseMutation]
  );

  const openNotesModal = useCallback(() => setShowNotesModal(true), []);
  const closeNotesModal = useCallback(() => setShowNotesModal(false), []);

  return {
    // Data
    handoffDetails,
    isLoading,
    error: queryError ? (queryError as Error).message : validationData?.error || null,

    // Actions
    handleClaim,
    handleDecline,
    handleClaimWithNotes,
    handleReleaseWithNotes,
    openNotesModal,
    closeNotesModal,
    showNotesModal,

    // Status
    isClaiming: claimMutation.isPending,
    isDeclining: declineMutation.isPending,
    isSubmittingNotes: claimWithNotesMutation.isPending || releaseMutation.isPending,
  };
};

export default useSmsHandoff;
